import type {
  GameState,
  MapDef,
  Owner,
  Player,
  PlayerId,
  TerritoryDef,
  TerritoryId,
} from './types';
import { rulesFor } from './rules';

/** Deep-copy plain game data. State is JSON-shaped by design. */
export function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function territoryDef(map: MapDef, id: TerritoryId): TerritoryDef {
  const t = map.territories.find((x) => x.id === id);
  if (!t) throw new Error(`Unknown territory ${id}`);
  return t;
}

export function areAdjacent(map: MapDef, a: TerritoryId, b: TerritoryId): boolean {
  return territoryDef(map, a).neighbors.includes(b);
}

export function ownedBy(state: GameState, owner: Owner): TerritoryId[] {
  return Object.values(state.territories)
    .filter((t) => t.owner === owner)
    .map((t) => t.id);
}

export function currentPlayer(state: GameState): Player {
  const p = state.players[state.currentPlayerIndex];
  if (!p) throw new Error('No current player');
  return p;
}

export function playerById(state: GameState, id: PlayerId): Player | undefined {
  return state.players.find((p) => p.id === id);
}

export function isPinned(state: GameState, id: TerritoryId): boolean {
  const t = state.territories[id];
  return !!t && t.zombies >= t.armies;
}

export function totalZombies(state: GameState): number {
  return Object.values(state.territories).reduce((n, t) => n + t.zombies, 0);
}

export function activePlayers(state: GameState): Player[] {
  return state.players.filter((p) => !p.eliminated);
}

/** Reinforcements owed to a player at the start of their turn. */
export function reinforcementsFor(state: GameState, map: MapDef, playerId: PlayerId): number {
  const rules = rulesFor(state.config.difficulty);
  const owned = ownedBy(state, playerId);
  let n = Math.max(
    rules.minReinforcements,
    Math.floor(owned.length / rules.territoriesPerReinforcement),
  );
  const ownedSet = new Set(owned);
  for (const r of map.regions) {
    if (r.territories.every((t) => ownedSet.has(t))) n += r.bonus;
  }
  return n;
}

/** Whether `to` is reachable from `from` through territories owned by `owner`. */
export function connectedThroughOwned(
  state: GameState,
  map: MapDef,
  owner: Owner,
  from: TerritoryId,
  to: TerritoryId,
): boolean {
  if (from === to) return false;
  const seen = new Set<TerritoryId>([from]);
  const stack = [from];
  while (stack.length) {
    const id = stack.pop()!;
    for (const n of territoryDef(map, id).neighbors) {
      if (seen.has(n)) continue;
      if (state.territories[n]?.owner !== owner) continue;
      if (n === to) return true;
      seen.add(n);
      stack.push(n);
    }
  }
  return false;
}

/** Territories adjacent to any territory the player owns, excluding their own. */
export function frontierOf(state: GameState, map: MapDef, playerId: PlayerId): TerritoryId[] {
  const out = new Set<TerritoryId>();
  for (const id of ownedBy(state, playerId)) {
    for (const n of territoryDef(map, id).neighbors) {
      if (state.territories[n]?.owner !== playerId) out.add(n);
    }
  }
  return [...out];
}
